import React, { useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { Upload, CheckCircle, AlertTriangle, FileUp } from "lucide-react";

const ENTITIES = [
  { key: "products", label: "Productos", cols: "reference, name, price, short_description, description, category_ids, image (o images: URLs separadas por comas), seo_title, meta_description" },
  { key: "customers", label: "Clientes", cols: "name, company, cif, email, phone, address, postal_code, city, province, country" },
  { key: "categories", label: "Categorías", cols: "name, description, seo_title, meta_description, image" },
];

export default function AdminImport() {
  const [entity, setEntity] = useState("products");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const doPreview = async () => {
    if (!file) { toast.error("Seleccione un archivo CSV"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await adminApi.post(`/admin/import/${entity}/preview`, fd);
      setPreview(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al previsualizar"); }
  };

  const commit = async () => {
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await adminApi.post(`/admin/import/${entity}/commit`, fd);
      toast.success(`${r.data.inserted} registros importados, ${r.data.skipped} omitidos`);
      setPreview(null);
      setFile(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al importar"); }
    finally { setImporting(false); }
  };

  const current = ENTITIES.find((e) => e.key === entity);

  return (
    <div className="max-w-3xl">
      <h1 className="font-heading font-bold text-3xl tracking-tighter mb-6">Importar CSV</h1>

      <div className="bg-white border border-border rounded-sm p-5 space-y-4">
        <div className="flex gap-2">
          {ENTITIES.map((e) => (
            <button key={e.key} onClick={() => { setEntity(e.key); setPreview(null); setFile(null); }} className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${entity === e.key ? "bg-brand text-white" : "bg-secondary text-muted-foreground"}`} data-testid={`import-tab-${e.key}`}>{e.label}</button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground bg-secondary/50 p-3 rounded-sm">Columnas esperadas: <span className="font-mono">{current.cols}</span></p>

        <label className="flex items-center gap-3 border-2 border-dashed border-border rounded-sm p-6 cursor-pointer hover:border-brand transition-colors">
          <FileUp className="h-6 w-6 text-brand" />
          <div>
            <p className="text-sm font-medium">{file ? file.name : "Seleccione un archivo CSV o Excel"}</p>
            <p className="text-xs text-muted-foreground">Formatos admitidos: .csv y .xlsx</p>
          </div>
          <input type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} data-testid="import-file-input" />
        </label>

        <button onClick={doPreview} className="inline-flex items-center gap-2 bg-brand-ink text-white rounded-sm px-4 py-2 text-sm font-semibold" data-testid="import-preview-btn"><Upload className="h-4 w-4" /> Previsualizar</button>
      </div>

      {preview && (
        <div className="bg-white border border-border rounded-sm p-5 mt-5">
          <div className="flex flex-wrap gap-4 mb-4">
            <span className="inline-flex items-center gap-1.5 text-sm"><CheckCircle className="h-4 w-4 text-emerald-600" /> {preview.valid} válidos de {preview.total}</span>
            {preview.errors.length > 0 && <span className="inline-flex items-center gap-1.5 text-sm text-amber-700"><AlertTriangle className="h-4 w-4" /> {preview.errors.length} con errores</span>}
          </div>
          {preview.errors.length > 0 && (
            <div className="max-h-40 overflow-y-auto bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4 text-xs space-y-1">
              {preview.errors.map((e, i) => <p key={i}>Fila {e.row}: {e.errors.join(", ")}</p>)}
            </div>
          )}
          <div className="overflow-x-auto max-h-64 border border-border rounded-sm">
            <table className="w-full text-xs">
              <thead className="bg-secondary sticky top-0"><tr>{Object.keys(preview.sample[0] || {}).map((k) => <th key={k} className="text-left px-2 py-1.5 whitespace-nowrap">{k}</th>)}</tr></thead>
              <tbody>{preview.sample.map((row, i) => <tr key={i} className="border-t border-border">{Object.values(row).map((v, j) => <td key={j} className="px-2 py-1 whitespace-nowrap max-w-[200px] truncate">{String(v)}</td>)}</tr>)}</tbody>
            </table>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={commit} disabled={importing} className="inline-flex items-center gap-2 bg-brand text-white rounded-sm px-5 py-2 text-sm font-semibold hover:bg-brand-dark disabled:opacity-60" data-testid="import-commit-btn">{importing ? "Importando..." : `Importar ${preview.valid} registros`}</button>
            <button onClick={() => setPreview(null)} className="border border-border rounded-sm px-5 py-2 text-sm hover:border-destructive" data-testid="import-cancel-btn">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}
